import { useCallback, useEffect, useState } from "react";

// ── Guidance progress store (localStorage) ───────────────────────────────────
// Tracks, per browser, which guides a user has seen/skipped/completed, whether
// they've turned guidance off entirely, and per-guide "remind me later" snoozes.
//
// Persistence is intentionally local-only for now; the whole store sits behind
// this hook so it can be swapped for a server-backed implementation later
// without touching any UI.
//
// Shape:
//   { disabled: boolean, guides: { [id]: "seen"|"skipped"|"completed" }, snoozed: { [id]: epochMs } }

const STORAGE_KEY = "ample_guidance_v1";
// Pre-existing FirstRunTour flag — migrate so veterans don't re-see the tour.
const LEGACY_TOUR_KEY = "ample_tour_completed";
const CHANGE_EVENT = "amplehire:guidance-changed";

const emptyState = () => ({ disabled: false, guides: {}, snoozed: {} });

function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const state =
      parsed && typeof parsed === "object"
        ? { ...emptyState(), ...parsed }
        : emptyState();
    if (
      !state.guides["core-tour"] &&
      localStorage.getItem(LEGACY_TOUR_KEY) === "true"
    ) {
      state.guides["core-tour"] = "completed";
    }
    return state;
  } catch {
    return emptyState();
  }
}

function write(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage blocked — state lives only in memory for this session */
  }
  // Let every mounted hook instance (layer + launcher) re-sync immediately.
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export default function useGuidanceProgress() {
  const [state, setState] = useState(read);

  useEffect(() => {
    const sync = () => setState(read());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync); // other tabs
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const setStatus = useCallback((id, status) => {
    const next = read();
    next.guides = { ...next.guides, [id]: status };
    write(next);
    setState(next);
  }, []);

  const snooze = useCallback((id, ms) => {
    const next = read();
    next.snoozed = { ...next.snoozed, [id]: Date.now() + ms };
    write(next);
    setState(next);
  }, []);

  const setDisabled = useCallback((disabled) => {
    const next = read();
    next.disabled = !!disabled;
    write(next);
    setState(next);
  }, []);

  const reset = useCallback(() => {
    const next = emptyState();
    write(next);
    setState(next);
  }, []);

  return { state, setStatus, snooze, setDisabled, reset };
}
